import { enrichedSettingsCategories, getEnrichedCategory } from './SettingsPage'
import { EnrichedSettingsCategory } from './util/SettingsConfigurations'
import { AppAction, Dispatch } from '../../types'
import MonitoringLocalStorageManager from '../../util/storage/MonitoringLocalStorageManager'

export enum ActionType {
  ChangeEnvironment = 'Monitoring/Settings/ChangeEnvironment',
  ChangeCategory = 'Monitoring/Settings/ChangeCategory',
}

// Basic Actions

export type ChangeEnvironmentAction = {
  type: ActionType.ChangeEnvironment
  payload: { category: EnrichedSettingsCategory, environmentId: string }
}

export type ChangeChangeCategory = {
  type: ActionType.ChangeCategory
  payload: { category: EnrichedSettingsCategory }
}

export type SettingsActions =
  | ChangeEnvironmentAction
  | ChangeChangeCategory

// Basic Actions Creators

export const changeEnvironmentAction = (category: EnrichedSettingsCategory, environmentId: string): AppAction => ({
  type: ActionType.ChangeEnvironment,
  payload: {category, environmentId}
})

export const changeCategoryAction = (category: EnrichedSettingsCategory): AppAction => ({
  type: ActionType.ChangeCategory,
  payload: {category}
})

// Thunks

export const changeEnvironment = (category: EnrichedSettingsCategory, environmentId: string) => (dispatch: Dispatch) => {
  dispatch(changeEnvironmentAction(category, environmentId))
  dispatch(handleRedirection(category.url, environmentId))
}

export const changeCategory = (category: EnrichedSettingsCategory) => (dispatch: Dispatch) => {
  dispatch(changeCategoryAction(category))
  dispatch(handleRedirection(category.url))
}

export const handleRedirection = (selectedCategory: string, selectedEnvironment?: string) =>  () => {
  const localStorageSettings = MonitoringLocalStorageManager.get('settings')
  const localStorageEnvId = localStorageSettings && localStorageSettings[selectedCategory] ?
    localStorageSettings[selectedCategory].selectedEnvironment : undefined
  
  let newLSSettings = {...localStorageSettings}
  newLSSettings.selectedCategory = selectedCategory
  if (selectedEnvironment) {
    newLSSettings[selectedCategory] = {selectedEnvironment: selectedEnvironment}
    window.location.hash = `#/settings/${selectedCategory}/environments/${selectedEnvironment}`
  } else if (localStorageEnvId) {
    newLSSettings[selectedCategory] = {selectedEnvironment: localStorageEnvId}
    window.location.hash = `#/settings/${selectedCategory}/environments/${localStorageEnvId}`
  } else {
    window.location.hash = `#/settings/${selectedCategory}`
  }
  
  MonitoringLocalStorageManager.put('settings', newLSSettings)
}

export const initSettings = (selectedCategory?: string, selectedEnvironment?: string) => (dispatch: Dispatch) => {
  const selectedEnrichedCategory = dispatch(initCategorySettings(selectedCategory))
  dispatch(initEnvironmentCategorySettings(selectedEnrichedCategory, selectedEnvironment))
}

export const initEnvironmentCategorySettings =
  (selectedEnrichedCategory: EnrichedSettingsCategory, selectedEnvironment?: string) => (dispatch: Dispatch) => {
  
  const localStorageSettings = MonitoringLocalStorageManager.get('settings')
  
  if (selectedEnvironment) {
    dispatch(changeEnvironment(selectedEnrichedCategory, selectedEnvironment))
  } else {
    switch (selectedEnrichedCategory.url) {
      case 'cloudhub':
        const selectedCloudhubEnv = localStorageSettings.cloudhub.selectedEnvironment
        if (selectedCloudhubEnv) dispatch(changeEnvironment(selectedEnrichedCategory, selectedCloudhubEnv))
        break
      
      case 'hybrid':
        const selectedHybridEnv = localStorageSettings.hybrid.selectedEnvironment
        if (selectedHybridEnv) dispatch(changeEnvironment(selectedEnrichedCategory, selectedHybridEnv))
        break
      
      default:
        break
    }
  }
}

export const initCategorySettings = (selectedCategory?: string) => (dispatch: Dispatch): EnrichedSettingsCategory => {
  let selectedEnrichedCategory
  const localStorageSettings = MonitoringLocalStorageManager.get('settings')
  const resolvedURLSettingsCategory = getEnrichedCategory(selectedCategory)
  
  if (resolvedURLSettingsCategory) {
    selectedEnrichedCategory = resolvedURLSettingsCategory
    dispatch(changeCategory(resolvedURLSettingsCategory))
  } else {
    const localStorageCategory = localStorageSettings ? localStorageSettings.selectedCategory : undefined
    const resolvedLocalStorageSettingsCategory = enrichedSettingsCategories.find(esc => esc.url === localStorageCategory)
    
    if (resolvedLocalStorageSettingsCategory) {
      selectedEnrichedCategory = resolvedLocalStorageSettingsCategory
      dispatch(changeCategory(resolvedLocalStorageSettingsCategory))
    }
    else {
      selectedEnrichedCategory = enrichedSettingsCategories[0]
      dispatch(changeCategory(enrichedSettingsCategories[0]))
    }
  }
  
  return selectedEnrichedCategory
}
